import { useNavigate } from "@tanstack/react-router"
import { motion, useReducedMotion } from "motion/react"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconTriangleWarningOutline18 as TriangleWarning,
	IconXmarkOutline18 as XmarkIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { IconButton, Tooltip } from "@productlane/ui"

import type { ResolutionCapWarning } from "./ai-resolution-cap-warning"
import { api } from "@/lib/api"
import { useAiResolutionCapBannerState } from "@/lib/states/global-state"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"
import { SIDEBAR_ITEM_BOX, sidebarTrackStyles } from "../../../sidebar-geometry"
import {
	agentDeployed,
	resolutionCapWarning,
	sidebarCapDisplay,
} from "./ai-resolution-cap-warning"

const WARNING_ICON = "text-yellow-500 dark:text-yellow-400"
// A nav row's box on the nav track puts the icon in the bell and gear column.
const SIDEBAR_MARK_BOX = `${SIDEBAR_ITEM_BOX} w-8`

function useResolutionCapDisplay() {
	const workspace = useZeroWorkspace()
	const { role } = useRole()
	const { dismissedKey, dismiss, reopen } = useAiResolutionCapBannerState()
	const watching = role === "ADMIN" && agentDeployed(workspace?.aiChatSettings)
	// Plan and trial status barely move; the live counters come from Zero.
	const { data: pricing } = api.workspace.aiResolutionPricing.useQuery(
		undefined,
		{ enabled: watching, staleTime: 10 * 60_000 },
	)

	const warning =
		watching && workspace && pricing
			? resolutionCapWarning({
					workspaceId: workspace.id,
					used: workspace.aiResolutionsUsedThisMonth,
					payPerUseEnabled: workspace.aiPayPerUseEnabled,
					monthlyLimit: workspace.aiResolutionMonthlyLimit ?? null,
					freeLimit: pricing.freeResolutions,
					trialing: pricing.trialing,
				})
			: null

	return {
		warning,
		display: sidebarCapDisplay(warning, dismissedKey),
		dismiss,
		reopen,
	}
}

// The hover panel widens over 200ms, so the card waits out most of that growth
// before it shows; its text would otherwise reflow in plain sight.
const CARD_REVEAL = {
	duration: 0.18,
	delay: 0.14,
	ease: [0.23, 1, 0.32, 1],
} as const

export function AiResolutionCapCard({
	warning,
	onDismiss,
	onAction,
}: {
	warning: Pick<
		ResolutionCapWarning,
		"heading" | "countLabel" | "progress" | "action"
	>
	onDismiss: () => void
	onAction: () => void
}) {
	const reduceMotion = useReducedMotion()

	return (
		<motion.div
			data-testid="ai-resolution-cap-banner"
			initial={reduceMotion ? false : { opacity: 0, y: 4 }}
			animate={{ opacity: 1, y: 0 }}
			transition={CARD_REVEAL}
			className="group relative mx-1.5 mb-2 flex select-none flex-col gap-2.5 rounded-lg bg-white p-3 shadow-md ring-1 ring-black/5 hover:bg-gray-50 hover:ring-black/10 dark:bg-gray-900 dark:ring-white/10 dark:hover:bg-gray-850 dark:hover:ring-white/15"
		>
			{/* A close button cannot sit inside a <button>, so the link covers the
			    card from underneath and the content lets clicks through to it. */}
			<button
				type="button"
				aria-label={`${warning.heading}, ${warning.countLabel}. ${warning.action}`}
				onClick={onAction}
				className="absolute inset-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
			/>
			<div className="pointer-events-none relative flex items-center gap-2">
				<TriangleWarning className={cn("h-4 w-4 flex-none", WARNING_ICON)} />
				<span className="min-w-0 flex-1 text-xs font-medium">
					{warning.heading}
				</span>
				<IconButton
					variant="ghost"
					size="sm"
					Icon={XmarkIcon}
					aria-label="Dismiss"
					className="pointer-events-auto -mr-1 flex-none"
					onClick={onDismiss}
				/>
			</div>
			<div
				role="progressbar"
				aria-label={warning.countLabel}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={Math.round(warning.progress * 100)}
				className="pointer-events-none relative h-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800"
			>
				<div
					className="h-full rounded-full bg-yellow-500 dark:bg-yellow-400"
					style={{ width: `${warning.progress * 100}%` }}
				/>
			</div>
			<div className="pointer-events-none relative flex items-center justify-between gap-2">
				<span className="whitespace-nowrap text-xxs tabular-nums text-tertiary">
					{warning.countLabel}
				</span>
				<ChevronRight className="h-3.5 w-3.5 flex-none text-tertiary group-hover:text-secondary" />
			</div>
		</motion.div>
	)
}

export function AiResolutionCapMark({
	heading,
	onClick,
}: {
	heading: string
	onClick: () => void
}) {
	return (
		<Tooltip label={heading} side="right">
			<IconButton
				variant="warning-ghost"
				Icon={TriangleWarning}
				className={SIDEBAR_MARK_BOX}
				iconClassName="h-4 w-4 flex-none"
				aria-label={heading}
				data-testid="ai-resolution-cap-icon"
				onClick={onClick}
			/>
		</Tooltip>
	)
}

export function AiResolutionCapBanner() {
	const navigate = useNavigate()
	const { warning, display, dismiss, reopen } = useResolutionCapDisplay()

	if (!warning) return null

	if (display.card) {
		return (
			<AiResolutionCapCard
				warning={warning}
				onDismiss={() => dismiss(warning.dismissKey)}
				onAction={() => void navigate({ href: warning.path })}
			/>
		)
	}

	if (!display.icon) return null

	return (
		<div className={cn(sidebarTrackStyles, "items-start pb-1")}>
			<AiResolutionCapMark heading={warning.heading} onClick={reopen} />
		</div>
	)
}

export function AiResolutionCapBannerCollapsed() {
	const navigate = useNavigate()
	const { warning, display } = useResolutionCapDisplay()

	if (!warning || !display.collapsedIcon) return null

	return (
		<div className={cn(sidebarTrackStyles, "items-start pb-1")}>
			<AiResolutionCapMark
				heading={warning.heading}
				onClick={() => void navigate({ href: warning.path })}
			/>
		</div>
	)
}
